import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Browser dictation via the Web Speech API — no backend, no keys, no cost.
 * Chrome/Edge/Safari expose it as `SpeechRecognition` (webkit-prefixed on
 * older builds); Firefox has none, hence the `supported` flag callers use to
 * hide the affordance entirely.
 *
 * Resolved once at module load — the constructor identity never changes.
 */
const SpeechRecognitionImpl =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null

/**
 * Errors the engine raises for reasons that usually clear on their own: the
 * speech service hiccuped (`network` — Chrome streams audio to Google's
 * recogniser, and that link drops for a second now and then, especially with
 * a WebRTC call on the same connection) or the microphone was momentarily
 * unavailable (`audio-capture` — typically the call's own capture grabbing the
 * device a beat earlier). While the user still wants to dictate these are
 * retried a few times with backoff before being surfaced; only a persistent
 * failure reaches the toast.
 */
const TRANSIENT_ERRORS = new Set(['network', 'audio-capture'])
/** Benign: silence timed out, or our own stop() — never worth a toast. */
const BENIGN_ERRORS = new Set(['no-speech', 'aborted'])
const RETRY_DELAYS_MS = [400, 1000, 2500]
/** A beat between the engine ending and a restart — start() right inside
 * onend throws InvalidStateError on some Chrome builds ("already started"). */
const RESTART_DELAY_MS = 250

/**
 * Continuous voice-to-text for a text field. Final (settled) phrases are handed
 * to `onResult` for the caller to append to its value; the still-forming phrase
 * is exposed as `interim` for a live caption. `onError` receives the raw Web
 * Speech error code (notably `not-allowed` when the mic permission is denied,
 * `network` / `audio-capture` once retries are exhausted).
 *
 *   const { supported, listening, interim, toggle } = useSpeechToText({
 *     onResult: (text) => setNote((n) => appendSpoken(n, text)),
 *   })
 *
 * `lang` defaults to en-IN to match the rest of the app's Indian-English
 * locale; the recogniser only listens in one language at a time.
 */
export function useSpeechToText({ onResult, onError, lang = 'en-IN' } = {}) {
  const supported = Boolean(SpeechRecognitionImpl)
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')

  const recognitionRef = useRef(null)
  // The engine stops itself after a pause even with continuous=true; this
  // tracks whether the USER still wants to dictate, so onend can tell an
  // auto-stop (restart) apart from a deliberate stop (settle to idle).
  const shouldListenRef = useRef(false)
  // Consecutive transient failures since the last recognised phrase.
  const retryCountRef = useRef(0)
  const timerRef = useRef(null)
  const onResultRef = useRef(onResult)
  const onErrorRef = useRef(onError)
  onResultRef.current = onResult
  onErrorRef.current = onError

  useEffect(() => {
    if (!supported) return undefined

    const recognition = new SpeechRecognitionImpl()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = lang

    const clearTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
    const startEngine = () => {
      try {
        recognition.start()
      } catch {
        // InvalidStateError: still winding down — onend will schedule another go.
      }
    }
    const scheduleStart = (delay) => {
      clearTimer()
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        if (shouldListenRef.current) startEngine()
      }, delay)
    }
    const settleIdle = () => {
      shouldListenRef.current = false
      clearTimer()
      setListening(false)
      setInterim('')
    }

    recognition.onresult = (event) => {
      let finalText = ''
      let interimText = ''
      // resultIndex points at the first result changed since the last event,
      // so we only ever read the new tail rather than re-emitting old phrases.
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        const transcript = result[0]?.transcript ?? ''
        if (result.isFinal) finalText += transcript
        else interimText += transcript
      }
      // Audio is flowing again — a later hiccup starts its retries afresh.
      retryCountRef.current = 0
      if (finalText) onResultRef.current?.(finalText)
      setInterim(interimText)
    }

    recognition.onerror = (event) => {
      const code = event?.error
      if (!code || BENIGN_ERRORS.has(code)) return
      if (TRANSIENT_ERRORS.has(code) && shouldListenRef.current) {
        const attempt = retryCountRef.current
        if (attempt < RETRY_DELAYS_MS.length) {
          // onend follows this error; its restart is suppressed in favour of
          // the backed-off one scheduled here.
          retryCountRef.current = attempt + 1
          setInterim('')
          scheduleStart(RETRY_DELAYS_MS[attempt])
          return
        }
      }
      // not-allowed / service-not-allowed / language-not-supported, or a
      // transient error that kept coming back: stop and tell the user why.
      settleIdle()
      onErrorRef.current?.(code)
    }

    recognition.onend = () => {
      setInterim('')
      if (!shouldListenRef.current) {
        setListening(false)
        return
      }
      // A retry is already scheduled (transient error) — let it run.
      if (timerRef.current) return
      // A mid-session auto-stop (silence / engine session limit): resume after
      // a beat so continuous dictation survives pauses.
      scheduleStart(RESTART_DELAY_MS)
    }

    recognitionRef.current = recognition
    return () => {
      shouldListenRef.current = false
      clearTimer()
      try {
        recognition.stop()
      } catch {
        /* not running */
      }
      recognitionRef.current = null
    }
  }, [supported, lang])

  const start = useCallback(() => {
    if (!supported || shouldListenRef.current) return
    shouldListenRef.current = true
    retryCountRef.current = 0
    try {
      recognitionRef.current?.start()
      setListening(true)
    } catch {
      // start() throws if the engine is momentarily still stopping — the next
      // toggle recovers it, so leave the flags for onend to reconcile.
    }
  }, [supported])

  const stop = useCallback(() => {
    shouldListenRef.current = false
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    try {
      recognitionRef.current?.stop()
    } catch {
      /* not running */
    }
    setListening(false)
    setInterim('')
  }, [])

  const toggle = useCallback(() => {
    if (shouldListenRef.current) stop()
    else start()
  }, [start, stop])

  return { supported, listening, interim, start, stop, toggle }
}

/**
 * Merge a freshly-dictated phrase into existing note text with tidy spacing:
 * a single separating space (unless the text already ends in whitespace) and a
 * capitalised first word when the note was empty. Blank additions are dropped.
 */
export function appendSpokenText(existing, addition) {
  const prev = existing ?? ''
  const chunk = String(addition ?? '').trim()
  if (!chunk) return prev
  if (!prev) return chunk.charAt(0).toUpperCase() + chunk.slice(1)
  return prev + (/\s$/.test(prev) ? '' : ' ') + chunk
}
